import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { PaymentRequest, StripePaymentRequestButtonElementClickEvent } from '@stripe/stripe-js';
import { ErrorType, recordError } from '../../../../models/apiWrappers/api/exceptions';
import { useAnalytics } from '../../../../utils/analytics';
import { getColor } from '../../../common/UICommon';
import { ThinLabel } from '../SignupWithPayment.style';
import { CreditCardFieldState } from '../SignupWithPayment.type';
import {
  CardDiv,
  CardFormWrapper,
  CardIfo,
  Date,
  ExpressCheckoutContainer,
  PaymentButtonContainer,
  PaymentButtonWrapper,
} from './PaymentForm.style';

enum CardInputType {
  Card = 'Card',
  Cvv = 'Cvv',
  Date = 'Date',
}

const getOptions = (variant: CardInputType) => {
  const options = {
    style: {
      base: {
        fontSize: '16px',
        fontWeight: '400',
        backgroundColor: getColor('WHITE'),
        '::placeholder': {
          color: getColor('BLACK_60'),
        },
      },
    },
    placeholder: '',
  };

  if (variant === CardInputType.Card) {
    options.placeholder = '1234 5678 1234 5678';
  }

  if (variant === CardInputType.Cvv) {
    options.placeholder = 'CVC';
  }

  if (variant === CardInputType.Date) {
    options.placeholder = 'MM / YY';
  }

  return options;
};

interface Props {
  paymentRequest: PaymentRequest | null;
  onChange: () => void;
  canExpressPaymentBeMade: () => boolean;
  cardNumberState: CreditCardFieldState;
  setCardNumberState: (stage: CreditCardFieldState) => void;
  cardExpState: CreditCardFieldState;
  setCardExpState: (stage: CreditCardFieldState) => void;
  cardCvcState: CreditCardFieldState;
  setCardCvcState: (stage: CreditCardFieldState) => void;
}

export const PaymentForm: React.FC<Props> = ({
  paymentRequest,
  canExpressPaymentBeMade,
  onChange,
  cardCvcState,
  cardExpState,
  cardNumberState,
  setCardCvcState,
  setCardExpState,
  setCardNumberState,
}) => {
  const analytics = useAnalytics();

  const handleChange = (
    currentState: string,
    setMethod: (stage: CreditCardFieldState) => void,
    complete: boolean,
    error: boolean
  ) => {
    onChange();
    if (complete && currentState !== 'valid') {
      return setMethod({ state: 'valid', error: false });
    }
    if (currentState !== 'unknown') {
      setMethod({ state: 'unknown', error: false });
    }
    if (error && currentState !== 'invalid') {
      return setMethod({ state: 'invalid', error: true });
    }
  };

  const onAppleGooglePaymentRequest = async (event: StripePaymentRequestButtonElementClickEvent) => {
    // important!
    // the actions should be fast
    // otherwise, it might blok Apple pay form (executed by paymentRequest.show())
    try {
      event.preventDefault();
      const result = canExpressPaymentBeMade();

      if (!result) {
        return;
      }

      paymentRequest?.show();
      analytics.track('pay with google apple pay click');
    } catch (error) {
      recordError(ErrorType.HANDLED, error);
    }
  };

  return (
    <CardFormWrapper>
      {paymentRequest && (
        <ExpressCheckoutContainer>
          <ThinLabel>Express checkout</ThinLabel>
          <PaymentButtonContainer>
            <PaymentButtonWrapper>
              <PaymentRequestButtonElement options={{ paymentRequest }} onClick={onAppleGooglePaymentRequest} />
            </PaymentButtonWrapper>
          </PaymentButtonContainer>
        </ExpressCheckoutContainer>
      )}

      {!paymentRequest && <ThinLabel>Pay with credit card</ThinLabel>}
      {paymentRequest && <ThinLabel>Or pay with credit card</ThinLabel>}
      <CardDiv className={`creditCard`} error={cardNumberState.error}>
        <CardNumberElement
          options={getOptions(CardInputType.Card)}
          onChange={(event) => {
            const { complete, error } = event;
            handleChange(cardNumberState.state, setCardNumberState, complete, !!error);
          }}
        />
      </CardDiv>

      <Date>
        <CardIfo className="exp">
          <CardDiv className={`other`} error={cardExpState.error}>
            <CardExpiryElement
              options={getOptions(CardInputType.Date)}
              onChange={(event) => {
                const { complete, error } = event;
                handleChange(cardExpState.state, setCardExpState, complete, !!error);
              }}
            />
          </CardDiv>
        </CardIfo>

        <CardIfo>
          <CardDiv className={`other`} error={cardCvcState.error}>
            <CardCvcElement
              options={getOptions(CardInputType.Cvv)}
              onChange={(event) => {
                const { complete, error } = event;
                handleChange(cardCvcState.state, setCardCvcState, complete, !!error);
              }}
            />
          </CardDiv>
        </CardIfo>
      </Date>
    </CardFormWrapper>
  );
};
